import React, { ReactElement, useState } from 'react';
import { Button, Heading, LoaderButton } from 'idsk-react-components';
import { MultiSelect } from 'slovlex-idsk-react-components';
import { SafeParameterSetter } from '../../../../types/SafeParameterSetter';
import LegislativnyMaterialCreateUpdate from '../../../../model/legislativnyMaterial/LegislativnyMaterialCreateUpdate';
import LegMaterialCreateStepEnum from '../../../../model/legislativnyMaterial/LegMaterialCreateStepEnum';
import CheckDataTable from '../component/CheckDataTable';
import {
  additionalAttributeKodList,
  arrayIncludesInObj,
  onlyHlavnyPredpisKodList,
} from '../../../../function/legMaterialFunctions';
import useSearchSuvisiaciProcesList from '../hook/useSearchSuvisiaciProcesList';
import SuvisiaciProces from '../../../../model/legislativnyMaterial/SuvisiaciProces';
import {
  MAX_MULTISELECT_RESULT,
  SHOW_ALL_MAX_RESULT,
} from '../../../../constants/MultiSelectConstant';
import useSearchNovelizovanyPredpisList from '../hook/useSearchNovelizovanyPredpisList';
import PredpisZbierky from '../../../../model/legislativnyMaterial/PredpisZbierky';

interface Props {
  legislativnyMaterial: LegislativnyMaterialCreateUpdate;
  onUpdateField: SafeParameterSetter<LegislativnyMaterialCreateUpdate>;
  loading: boolean;
  onChangeStep: (nextStep: LegMaterialCreateStepEnum) => Promise<void>;
  isUpdate?: boolean;
}

const RelatedDataStepThree = ({
  onUpdateField,
  legislativnyMaterial,
  loading,
  onChangeStep,
  isUpdate = false,
}: Props): ReactElement => {
  const [searchTextSuvisiaciProces, setSearchTextSuvisiaciProces] = useState('');
  const [searchTextNovelizovanyPredpis, setSearchTextNovelizovanyPredpis] = useState('');
  const [searchTextHlavnyNovelizovanyPredpis, setSearchTextHlavnyNovelizovanyPredpis] = useState('');
  const [limit, setLimit] = useState(MAX_MULTISELECT_RESULT);
  const [limitList, setLimitList] = useState(MAX_MULTISELECT_RESULT);
  const [limitProces, setLimitProces] = useState(MAX_MULTISELECT_RESULT);
  const {
    suvisiaciProcesList,
    suvisiaciProcesListStatus,
  } = useSearchSuvisiaciProcesList(
    searchTextSuvisiaciProces,
    limitProces,
  );

  const {
    novelizovanyPredpisList: hlavnyNovelizovanyPredpisList,
    novelizovanyPredpisListStatus: hlavnyNovelizovanyPredpisListStatus,
  } = useSearchNovelizovanyPredpisList(searchTextHlavnyNovelizovanyPredpis, limit);

  const {
    novelizovanyPredpisList,
    novelizovanyPredpisListStatus,
  } = useSearchNovelizovanyPredpisList(searchTextNovelizovanyPredpis, limitList);

  return (
    <div>
      <Heading className="page-title" size="xl">
        {isUpdate ? 'Úprava súvisiacich informácií' : 'Vyplňte súvisiace informácie'}
      </Heading>
      <MultiSelect
        loading={suvisiaciProcesListStatus.loading}
        handleShowAll={() => {
          setLimitProces(SHOW_ALL_MAX_RESULT);
        }}
        withShowAllButton={(limitProces === MAX_MULTISELECT_RESULT
            && suvisiaciProcesList.length === MAX_MULTISELECT_RESULT)
          || (limitProces === SHOW_ALL_MAX_RESULT && suvisiaciProcesList.length <= MAX_MULTISELECT_RESULT)}
        searchText={searchTextSuvisiaciProces}
        setSearchText={(text) => {
          setSearchTextSuvisiaciProces(text);
          setLimitProces(MAX_MULTISELECT_RESULT);
        }}
        label="Súvisiace procesy"
        hint="Začnite písať a vyberte z ponúkaných možností."
        itemListAttribute="cisloLpNazov"
        width="three-quarters"
        data={suvisiaciProcesList}
        required={false}
        dataAttributesToShow={[{
          key: 'cisloLpNazov',
          label: '',
        }]}
        selectedItemList={legislativnyMaterial.suvisiaceProcesy}
        setSelectedItemList={(selectedItems: SuvisiaciProces[]) => {
          onUpdateField('suvisiaceProcesy', selectedItems
            .sort((a, b) => Intl.Collator()
              .compare(a.cisloLpNazov, b.cisloLpNazov)));
        }}
        maxResults={SHOW_ALL_MAX_RESULT}
      />
      {(arrayIncludesInObj(additionalAttributeKodList, legislativnyMaterial.typ.kod)) && !isUpdate && (
        <>
          <MultiSelect
            loading={hlavnyNovelizovanyPredpisListStatus.loading}
            handleShowAll={() => {
              setLimit(SHOW_ALL_MAX_RESULT);
            }}
            withShowAllButton={(limit === MAX_MULTISELECT_RESULT
                && hlavnyNovelizovanyPredpisList.length === MAX_MULTISELECT_RESULT)
              || (limit === SHOW_ALL_MAX_RESULT && hlavnyNovelizovanyPredpisList.length <= MAX_MULTISELECT_RESULT)}
            searchText={searchTextHlavnyNovelizovanyPredpis}
            setSearchText={(text) => {
              setSearchTextHlavnyNovelizovanyPredpis(text);
              setLimit(MAX_MULTISELECT_RESULT);
            }}
            label="Hlavný novelizovaný predpis"
            required={false}
            hint="Začnite písať a vyberte z ponúkaných možností."
            width="three-quarters"
            data={hlavnyNovelizovanyPredpisList}
            itemListAttribute="popis"
            dataAttributesToShow={[{
              key: 'popis',
              label: '',
            }]}
            selectedItemList={legislativnyMaterial.hlavnyNovelizovanyPredpis
              ? [legislativnyMaterial.hlavnyNovelizovanyPredpis] : []}
            setSelectedItemList={(selectedItems: PredpisZbierky[]) => {
              onUpdateField('hlavnyNovelizovanyPredpis', selectedItems[0]);
            }}
            maxResults={SHOW_ALL_MAX_RESULT}
            maxLength={1}
          />
          {!(arrayIncludesInObj(onlyHlavnyPredpisKodList, legislativnyMaterial.typ.kod)) && !isUpdate && (
          <MultiSelect
            loading={novelizovanyPredpisListStatus.loading}
            handleShowAll={() => {
              setLimitList(SHOW_ALL_MAX_RESULT);
            }}
            withShowAllButton={(limitList === MAX_MULTISELECT_RESULT
                && novelizovanyPredpisList.length === MAX_MULTISELECT_RESULT)
              || (limitList === SHOW_ALL_MAX_RESULT && novelizovanyPredpisList.length <= MAX_MULTISELECT_RESULT)}
            searchText={searchTextNovelizovanyPredpis}
            setSearchText={(text) => {
              setSearchTextNovelizovanyPredpis(text);
              setLimitList(MAX_MULTISELECT_RESULT);
            }}
            label="Ďalšie novelizované predpisy"
            required={false}
            hint="Začnite písať a vyberte z ponúkaných možností."
            width="three-quarters"
            data={novelizovanyPredpisList}
            itemListAttribute="popis"
            dataAttributesToShow={[{
              key: 'popis',
              label: '',
            }]}
            selectedItemList={legislativnyMaterial.novelizovanePredpisy}
            setSelectedItemList={(selectedItems: PredpisZbierky[]) => {
              onUpdateField('novelizovanePredpisy', selectedItems
                .sort((a, b) => Intl.Collator()
                  .compare(a.popis, b.popis)));
            }}
            maxResults={SHOW_ALL_MAX_RESULT}
          />
          )}
        </>
      )}
      <LoaderButton
        className="legislativny-material-create__submit-button"
        loading={loading}
        type="submit"
        size="large"
        onClick={() => onChangeStep(legislativnyMaterial.typ?.kod === 'AktEU'
          ? LegMaterialCreateStepEnum.CHECK_DATA_5 : LegMaterialCreateStepEnum.PREDKLADATEL_4)}
        disableOutsideClickWhileLoading
      >Pokračovať
      </LoaderButton>
      <Button
        className="legislativny-material__button"
        type="button"
        disabled={loading}
        buttonStyle="third"
        size="large"
        onClick={() => {
          onChangeStep(LegMaterialCreateStepEnum.BASIC_DATA_2);
        }}
      >
        Späť
      </Button>
      {isUpdate && legislativnyMaterial.typ?.kod !== 'AktEU' && (
        <Button
          className="legislativny-material__button"
          type="button"
          disabled={loading}
          buttonStyle="third"
          size="large"
          onClick={() => {
            onChangeStep(LegMaterialCreateStepEnum.CHECK_DATA_5);
          }}
        >
          Prejsť na zhrnutie
        </Button>
      )}
      <Heading size="m">Vaše odpovede</Heading>
      <CheckDataTable
        legislativnyMaterial={legislativnyMaterial}
        onChangeStep={onChangeStep}
        isUpdate={isUpdate}
      />
    </div>
  );
};

export default RelatedDataStepThree;
